// src/components/ReturnsPartsEntryV2.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import ReturnsPhotosV2 from "./ReturnsPhotosV2";
import { useParams } from "react-router-dom";

const RETURNS_COL = "returns_v2";

const SECTIONS = [
  { id: "return-part", label: "Return & Part" },
  { id: "codes", label: "Reported Reason & Fault Found" },
  { id: "procedure", label: "Test Procedure" },
  { id: "notes", label: "Notes" },
  { id: "photos", label: "Photos" },
];

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "waiting_collection", label: "Waiting for collection" },
  { value: "waiting_processing", label: "Waiting to be processed" },
  { value: "in_progress", label: "In progress" },
  { value: "straggler", label: "Straggler" },
];

const INITIAL_FORM = {
  returnsNoteNumber: "",
  serialNumber: "",
  sretNumber: "",
  partCode: "",
  partTitle: "",
  partDescription: "",
  reportedCode: "",
  faultFoundCode: "",
  internalNotes: "",
};

const INITIAL_TEST = {};

const COUNTERS_REF = doc(db, "appData", "counters");
function formatReturnsNoteNumber(n) {
  return `RN-${String(n).padStart(6, "0")}`;
}
async function getNextReturnsNumber() {
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(COUNTERS_REF);
    if (!snap.exists()) {
      tx.set(COUNTERS_REF, { despatchCounter: 0, returnsCounter: 1 });
      return 1;
    }
    const cur = Number(snap.data().returnsCounter || 0);
    const n = cur + 1;
    tx.update(COUNTERS_REF, { returnsCounter: n });
    return n;
  });
  return next;
}

function useDebouncedEffect(fn, deps, delay) {
  useEffect(() => {
    const t = setTimeout(fn, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* =========================================================
   SEARCH INDEX (universal)
========================================================= */

function normalize(str) {
  return String(str || "").trim().toLowerCase();
}

function digitsOnly(str) {
  return String(str || "").replace(/\D+/g, "");
}

function prefixes(str, min = 3, max = 16) {
  const s = normalize(str).replace(/\s+/g, "");
  const out = [];
  for (let i = min; i <= Math.min(max, s.length); i++) out.push(s.slice(0, i));
  return out;
}

function wordTokens(str) {
  const s = normalize(str);
  if (!s) return [];
  return s
    .split(/[^a-z0-9]+/g)
    .filter(Boolean)
    .slice(0, 40);
}

function buildReturnSearchIndexForParts({ form, template }) {
  const serial = normalize(form.serialNumber);
  const rnRaw = normalize(form.returnsNoteNumber);
  const rnDigits = digitsOnly(form.returnsNoteNumber);
  const sret = normalize(form.sretNumber);

  const partCode = normalize(form.partCode || template?.partCode);
  const partTitle = normalize(form.partTitle || template?.title);
  const partDesc = normalize(form.partDescription || template?.description);

  const tokens = new Set();

  // identifiers (prefix search)
  prefixes(serial).forEach((p) => tokens.add(`sn:${p}`));
  prefixes(rnRaw).forEach((p) => tokens.add(`rn:${p}`));
  prefixes(rnDigits).forEach((p) => tokens.add(`rnn:${p}`));
  prefixes(sret).forEach((p) => tokens.add(`sret:${p}`));
  prefixes(partCode).forEach((p) => tokens.add(`pc:${p}`));

  // word search
  [
    form.internalNotes,
    form.reportedCode,
    form.faultFoundCode,
    partCode,
    partTitle,
    partDesc,
  ].forEach((v) => wordTokens(v).forEach((w) => tokens.add(`w:${w}`)));

  return {
    serialLower: serial || "",
    rnDigits: rnDigits || "",
    sretLower: sret || "",
    partCodeLower: partCode || "",
    searchTokens: Array.from(tokens).slice(0, 500),
  };
}


export default function ReturnsPartsEntryV2() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const { id: routeId } = useParams();
  const urlId = routeId || qs.get("id") || qs.get("draft");

  const [form, setForm] = useState(INITIAL_FORM);
  const [test, setTest] = useState(INITIAL_TEST);
  const [template, setTemplate] = useState(null);

  const [status, setStatus] = useState("new");
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errors, setErrors] = useState({});

  const [returnsId, setReturnsId] = useState(urlId || null);

  const snapshotKey = useMemo(
    () => JSON.stringify({ form, test, templateCode: template?.partCode || "", status }),
    [form, test, template, status]
  );
  const [lastSavedKey, setLastSavedKey] = useState(snapshotKey);
  const dirty = snapshotKey !== lastSavedKey;

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);

  // side-nav highlight
  const observer = useRef(null);
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -70% 0px", threshold: 0 };
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id));
    }, opts);

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });

    return () => observer.current?.disconnect();
  }, []);

  const loadTemplate = async (partCode) => {
    const code = (partCode || "").trim();
    if (!code) {
      setTemplate(null);
      return;
    }

    const ref = doc(db, "part_templates", code);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      setTemplate(null);
      setForm((prev) => ({
        ...prev,
        partCode: code,
        partTitle: "",
        partDescription: "",
        reportedCode: "",
        faultFoundCode: "",
      }));
      setTest({});
      return;
    }

    const tpl = snap.data();
    setTemplate(tpl);

    setForm((prev) => ({
      ...prev,
      partCode: tpl.partCode,
      partTitle: tpl.title || "",
      partDescription: tpl.description || "",
      reportedCode: "",
      faultFoundCode: "",
    }));

    setTest((prev) => {
      const next = { ...prev };
      (tpl.steps || []).forEach((step) => {
        if (!(step.id in next)) next[step.id] = step.type === "check" ? false : "";
      });
      Object.keys(next).forEach((k) => {
        if (!(tpl.steps || []).some((s) => s.id === k)) delete next[k];
      });
      return next;
    });
  };

  // load existing record
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!urlId) return;

      const snap = await getDoc(doc(db, RETURNS_COL, urlId));
      if (!alive) return;

      if (!snap.exists()) {
        navigate("/returns/parts/new", { replace: true });
        return;
      }

      const d = snap.data();
      if (d.type && d.type !== "parts") {
        alert("That record is not a parts return.");
        navigate("/returns/parts/new", { replace: true });
        return;
      }

      d.parts?.form && setForm(d.parts.form);
      d.parts?.test && setTest(d.parts.test);
      if (d.parts?.templateSnapshot?.partCode) setTemplate(d.parts.templateSnapshot);

      setReturnsId(urlId);
      setStatus(d.status || "new");
      setStatusUpdatedAt(d.statusUpdatedAt || null);

      setLastSavedKey(
        JSON.stringify({
          form: d.parts?.form || {},
          test: d.parts?.test || {},
          templateCode: d.parts?.templateSnapshot?.partCode || "",
          status: d.status || "new",
        })
      );

      // if template missing but code exists, reload it
      if (!d.parts?.templateSnapshot?.partCode && d.parts?.form?.partCode) {
        await loadTemplate(d.parts.form.partCode);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  const codesActive = useMemo(
    () => (template?.codes || []).filter((c) => !c.deprecated),
    [template]
  );

  const progress = useMemo(() => {
    let s = 0;
    if (form.returnsNoteNumber) s += 25;
    if (form.serialNumber) s += 25;
    if (template?.partCode) s += 10;
    if (form.reportedCode) s += 20;
    if (form.faultFoundCode) s += 20;

    if (template?.steps?.length) {
      const total = template.steps.length;
      const done = template.steps.filter((st) => {
        const v = test[st.id];
        return st.type === "check" ? !!v : String(v || "").trim().length > 0;
      }).length;
      s += Math.min(20, Math.round((done / total) * 20));
    }
    return Math.min(100, s);
  }, [form, test, template]);

  const validate = () => {
    const e = {};
    if (!form.serialNumber?.trim()) e.serialNumber = "Serial number is required.";
    if (!form.partCode?.trim()) e.partCode = "Enter a part code and load the template.";
    if (!template?.partCode) e.template = "No template loaded for that part code.";
    if (!form.reportedCode) e.reportedCode = "Pick what was reported.";
    if (!form.faultFoundCode) e.faultFoundCode = "Pick what you found.";

    (template?.steps || []).forEach((step) => {
      if (!step.required) return;
      const val = test[step.id];
      const missing = step.type === "check" ? !val : String(val || "").trim() === "";
      if (missing) e[`step:${step.id}`] = `Required: ${step.label}`;
    });

    return e;
  };

  const ensureExists = async () => {
    if (returnsId) return returnsId;

    let ensured = form;
    if (!ensured.returnsNoteNumber) {
      const nextNum = await getNextReturnsNumber();
      ensured = { ...ensured, returnsNoteNumber: formatReturnsNoteNumber(nextNum) };
      setForm(ensured);
    }

    const searchIndex = buildReturnSearchIndexForParts({ form: ensured, template });

    const created = await addDoc(collection(db, RETURNS_COL), {
      type: "parts",

      ...searchIndex,

      status,
      statusUpdatedAt: statusUpdatedAt || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      submittedAt: null,
      archive: false,
      archivedAt: null,
      progress,
      source: "pthelpdesk",
      version: 1,
      parts: {
        form: ensured,
        test,
        templateSnapshot: template
          ? {
              partCode: template.partCode,
              title: template.title || "",
              description: template.description || "",
              codes: template.codes || [],
              steps: template.steps || [],
            }
          : null,
      },
    });

    setReturnsId(created.id);

    const q = new URLSearchParams(window.location.search);
    q.set("id", created.id);
    navigate({ pathname: "/returns/parts/new", search: `?${q.toString()}` }, { replace: true });

    // create sharepoint folder (non-fatal)
    try {
      const create = httpsCallable(fns, "spEnsureFolder");
      const { data } = await create({ type: "returnsImage", subId: created.id });
      await updateDoc(doc(db, RETURNS_COL, created.id), {
        storage: { provider: "sharepoint", folderId: data.folderItemId, folderUrl: data.folderWebUrl },
      });
    } catch (e) {
      console.error("Folder create failed:", e);
    }

    return created.id;
  };

  const save = async (manual = false) => {
    try {
      setSaveError("");
      setIsSaving(true);
      const searchIndex = buildReturnSearchIndexForParts({ form, template });
      const id = await ensureExists();
      await updateDoc(doc(db, RETURNS_COL, id), {
        type: "parts",

        ...searchIndex,

        status,
        statusUpdatedAt: statusUpdatedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        progress,
        parts: {
          form,
          test,
          templateSnapshot: template
            ? {
                partCode: template.partCode,
                title: template.title || "",
                description: template.description || "",
                codes: template.codes || [],
                steps: template.steps || [],
              }
            : null,
        },
      });

      setLastSavedKey(snapshotKey);
      if (manual) console.info("Saved.");
    } catch (e) {
      console.error(e);
      setSaveError("Could not save.");
    } finally {
      setIsSaving(false);
    }
  };

  // autosave
  useDebouncedEffect(() => {
    if (!dirty) return;
    if (isSaving || isSubmitting) return;
    if (!returnsId && progress === 0) return; // avoid creating empty docs
    save(false);
  }, [snapshotKey], 1500);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const eMap = validate();
    setErrors(eMap);

    if (Object.keys(eMap).length) {
      const first = Object.keys(eMap)[0];
      const target = first.startsWith("step:") ? "procedure" : (first === "reportedCode" || first === "faultFoundCode") ? "codes" : "return-part";
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    try {
      setIsSubmitting(true);
      const id = await ensureExists();
      const searchIndex = buildReturnSearchIndexForParts({ form, template });
      
      await updateDoc(doc(db, RETURNS_COL, id), {
        ...searchIndex,
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (!window.confirm("Submitted. Start a new report?")) return;
      navigate("/returns/parts/new", { replace: true });
      window.location.reload();
    } catch (err) {
      console.error(err);
      setErrors((p) => ({ ...p, submit: "Submit failed. Try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-900">Returns — Parts (V2)</h1>

        <div className="ml-0 sm:ml-4">
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setStatusUpdatedAt(new Date().toISOString());
            }}
            className="text-sm rounded-md border border-gray-300 px-3 py-2"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <span className="ml-auto text-sm text-gray-500">
          {isSaving ? "Saving…" : dirty ? "Unsaved changes" : returnsId ? "All changes saved" : "Nothing saved yet"}
        </span>

        {saveError && <span className="text-sm text-red-600">{saveError}</span>}

        <button
          type="button"
          onClick={() => save(true)}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Save
        </button>

        <button
          type="button"
          onClick={() => navigate("/ReturnsViewerV2")}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                className={`w-full text-left px-3 py-2 rounded-md transition ${
                  activeId === id ? "bg-indigo-600 text-white" : "hover:bg-gray-100 text-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div>
              <b>ID:</b> {returnsId ? returnsId.slice(-8) : "none"}
            </div>
            <div>
              <b>RN:</b> {form.returnsNoteNumber || "not allocated yet"}
            </div>
            <div>
              <b>Progress:</b> {Math.round(progress)}%
            </div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={onSubmit} className="space-y-10">
          {/* Return & Part */}
          <Section id="return-part" title="Return & Part">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Returns Note Number" value={form.returnsNoteNumber} onChange={() => {}} readOnly />

              <TextField
                label="Serial Number"
                value={form.serialNumber}
                onChange={(v) => setForm((p) => ({ ...p, serialNumber: v }))}
                error={errors.serialNumber}
              />

              <TextField
                label="SRET Number"
                value={form.sretNumber}
                onChange={(v) => setForm((p) => ({ ...p, sretNumber: v }))}
              />

              <div className="sm:col-span-2 grid grid-cols-[1fr_auto] gap-2">
                <TextField
                  label="Part Code"
                  value={form.partCode}
                  onChange={(v) => setForm((p) => ({ ...p, partCode: v }))}
                  error={errors.partCode || errors.template}
                />
                <div className="self-end">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                    onClick={async () => {
                      await loadTemplate(form.partCode);
                      // force save soon (don’t wait for debounce if user expects immediate)
                      // but don’t create empty docs: only if something meaningful
                      if ((form.partCode || "").trim()) save(false);
                    }}
                  >
                    Load
                  </button>
                </div>
              </div>

              <TextField label="Title" value={form.partTitle} onChange={() => {}} readOnly />
              <TextField label="Description" value={form.partDescription} onChange={() => {}} readOnly />
            </div>
          </Section>

          {/* Codes */}
          <Section id="codes" title="Reported Reason & Fault Found">
            {!template ? (
              <p className="text-sm text-gray-500">Load a part template to pick codes.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <CodeSelect
                  label="Reported"
                  value={form.reportedCode}
                  onChange={(code) => setForm((p) => ({ ...p, reportedCode: code }))}
                  options={codesActive}
                  getOptionLabel={(c) => `${c.code} — ${c.description}`}
                  getOptionValue={(c) => c.code}
                  placeholder="Search code or description…"
                  error={errors.reportedCode}
                />
                <CodeSelect
                  label="Fault Found"
                  value={form.faultFoundCode}
                  onChange={(code) => setForm((p) => ({ ...p, faultFoundCode: code }))}
                  options={codesActive}
                  getOptionLabel={(c) => `${c.code} — ${c.description}`}
                  getOptionValue={(c) => c.code}
                  placeholder="Search code or description…"
                  error={errors.faultFoundCode}
                />
              </div>
            )}
          </Section>

          {/* Procedure */}
          <Section id="procedure" title="Test Procedure">
            {!template ? (
              <p className="text-sm text-gray-500">Load a part template to see the test procedure.</p>
            ) : (template.steps || []).length === 0 ? (
              <p className="text-sm text-gray-500">No procedure steps defined for this template.</p>
            ) : (
              <div className="grid gap-3">
                {(template.steps || []).map((step) => {
                  const errKey = `step:${step.id}`;
                  const err = errors[errKey];

                  if (step.type === "check") {
                    return (
                      <div key={step.id}>
                        <CheckRow
                          label={`${step.label}${step.required ? " *" : ""}`}
                          checked={!!test[step.id]}
                          onChange={() => setTest((p) => ({ ...p, [step.id]: !p[step.id] }))}
                        />
                        {err && <FieldError text={err} />}
                      </div>
                    );
                  }

                  return (
                    <div key={step.id}>
                      <TextField
                        label={`${step.label}${step.required ? " *" : ""}`}
                        value={test[step.id] || ""}
                        onChange={(v) => setTest((p) => ({ ...p, [step.id]: v }))}
                        placeholder={step.placeholder || ""}
                        error={err}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Notes */}
          <Section id="notes" title="Notes">
            <TextArea
              label="Internal Notes"
              value={form.internalNotes}
              onChange={(v) => setForm((p) => ({ ...p, internalNotes: v }))}
              placeholder="Anything extra the workshop / tech team should know…"
            />
          </Section>

          {/* Photos */}
          <ReturnsPhotosV2 returnsId={returnsId} returnsCol={RETURNS_COL} />

          {/* Sticky submit */}
          <div className="h-16" />
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                ID: {returnsId ? returnsId.slice(-8) : "none"} · Status{" "}
                {STATUS_OPTIONS.find((s) => s.value === status)?.label || status} · Progress{" "}
                {Math.round(progress)}%
              </span>

              <button
                type="button"
                onClick={() => save(true)}
                className="ml-auto px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50"
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : "Save"}
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-md text-white transition ${
                  isSubmitting ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSubmitting ? "Submitting…" : "Submit Return"}
              </button>
            </div>
          </div>

          {errors.submit && <FieldError text={errors.submit} />}
          <div className="h-24" />
        </form>
      </div>
    </div>
  );
}

/* =========================================================
   UI BITS (same style as your other V2 screens)
========================================================= */

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, placeholder = "", error, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        } ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        value={value || ""}
        onChange={(e) => {
          if (!readOnly) onChange(e.target.value);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={4}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
      <input type="checkbox" className="h-4 w-4" checked={!!checked} onChange={onChange} />
      <span className="text-sm text-gray-800">{label}</span>
    </label>
  );
}

function FieldError({ text }) {
  return <p className="mt-1 text-sm text-red-600">{text}</p>;
}

function CodeSelect({ label, value, onChange, options, getOptionLabel, getOptionValue, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(term));
  }, [q, options, getOptionLabel]);

  const selected = options.find((o) => getOptionValue(o) === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      <div
        className={`w-full rounded-md border px-3 py-2 flex items-center justify-between cursor-pointer ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm text-gray-800">
          {selected ? getOptionLabel(selected) : <span className="text-gray-400">Select…</span>}
        </span>
        <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20">
          <path d="M5 7l5 5 5-5H5z" />
        </svg>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2">
            <input
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={placeholder || "Search…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-56 overflow-auto">
            {list.length === 0 && <div className="p-3 text-sm text-gray-500">No matches</div>}
            {list.map((o) => {
              const val = getOptionValue(o);
              return (
                <button
                  key={val}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    val === value ? "bg-gray-100" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(val);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {getOptionLabel(o)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <FieldError text={error} />}
    </div>
  );
}
